import React, { useState } from 'react';
import WorkflowForm from '../components/workflow-form.component';
import { WorkflowFormData } from '../types/workflow.types';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useWorkflowCreate } from '../hooks/use-workflow.hook';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';
import { useActiveTenant } from '@states/active-tenant.atom';
import PageTitle from '@components/common/PageTitle';

const WorkflowCreateContainer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeTenant } = useActiveTenant();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: createWorkflow, isPending } = useWorkflowCreate();

  const handleSubmit = async (data: WorkflowFormData) => {
    setIsLoading(true);

    if (!activeTenant) {
      message.error('No tenant selected');
      setIsLoading(false);
      return;
    }

    try {
      // Clean steps - remove undefined iframeContext
      const cleanedSteps = data.steps.map((step) => {
        const cleanedStep = { ...step };
        if (!cleanedStep.step.iframeContext?.autoDetect) {
          delete cleanedStep.step.iframeContext;
        }
        return cleanedStep;
      });

      const response = await createWorkflow({
        tenantId: activeTenant.id,
        name: data.name,
        steps: cleanedSteps,
        mappings: data.mappings,
        condition: data.condition,
      });

      message.success(t('crud.create.success', { entity: 'Workflow' }));
      navigate(`/dashboard/workflows/${response.data.id}`);
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.create.error', { entity: 'Workflow' })
      );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageTitle title={t('crud.create.title', { entity: t('workflows.title') })} />
      <HeaderRow
        title={t('crud.create.title', { entity: 'Workflow' })}
        showBackButton={true}
        showBreadcrumbs={true}
      />
      <div className="row mt-3">
        <WorkflowForm onSubmit={handleSubmit} isLoading={isLoading || isPending} />
      </div>
    </>
  );
};

export default WithPermissionCheck(WorkflowCreateContainer, 'workflow.create');
